import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

const EXCLUDED_PRODUCT_CONTEXT = 'product-card, [id*="QuickAdd-"], quick-add-component, dialog';
const STAR_SELECTION_EVENT = 'star:pdp-selection-change';

/**
 * Returns whether a product-selection event belongs to the main product page.
 * @param {Event} event
 * @returns {boolean}
 */
function isMainProductEvent(event) {
  return event.target instanceof Element && !event.target.closest(EXCLUDED_PRODUCT_CONTEXT) && 'promise' in event;
}

/**
 * Returns a same-origin relative URL where possible, preserving external HTTPS URLs.
 * @param {URL} url
 * @returns {string}
 */
function serializeActionUrl(url) {
  if (url.origin === window.location.origin) return `${url.pathname}${url.search}${url.hash}`;
  return url.toString();
}

class StarProductSupport extends HTMLElement {
  /** @type {AbortController | null} */
  #eventController = null;

  connectedCallback() {
    this.#eventController?.abort();
    this.#eventController = new AbortController();
    const { signal } = this.#eventController;
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(STAR_SELECTION_EVENT, this.#handleSelectionChange, { signal });
    document.addEventListener('click', this.#handleJump, { signal });
    window.addEventListener('hashchange', this.#handleHashChange, { signal });
    this.addEventListener('toggle', this.#handleDisclosureToggle, { capture: true, signal });
    this.#syncDisclosureState();

    const quantityInput = document.querySelector('[data-star-pdp] input[name="quantity"]');
    const currentQuantity = quantityInput instanceof HTMLInputElement ? quantityInput.value : this.dataset.quantity;
    this.#updateContextLinks(currentQuantity);
    requestAnimationFrame(() => {
      if (this.isConnected) this.#openAnchor(window.location.hash, false, false);
    });
  }

  disconnectedCallback() {
    this.#eventController?.abort();
    this.#eventController = null;
  }

  #syncDisclosureState() {
    for (const detail of this.querySelectorAll('details.star-product-support__detail')) {
      detail.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(detail.open));
    }
  }

  #handleDisclosureToggle = (event) => {
    if (event.target instanceof HTMLDetailsElement) this.#syncDisclosureState();
  };

  #openAnchor(hash, focus = true, scroll = true) {
    if (!hash?.startsWith('#')) return false;
    let target;
    try {
      target = document.getElementById(decodeURIComponent(hash.slice(1)));
    } catch {
      return false;
    }
    if (!(target instanceof HTMLElement) || !this.contains(target)) return false;

    let ancestor = target;
    while (ancestor && this.contains(ancestor)) {
      if (ancestor instanceof HTMLDetailsElement) ancestor.open = true;
      ancestor = ancestor.parentElement;
    }
    this.#syncDisclosureState();

    if (focus) {
      const focusTarget = target instanceof HTMLDetailsElement ? target.querySelector(':scope > summary') : target;
      if (focusTarget instanceof HTMLElement) {
        if (!focusTarget.matches('summary, a, button, input, select, textarea, [tabindex]')) {
          focusTarget.setAttribute('tabindex', '-1');
        }
        focusTarget.focus({ preventScroll: true });
      }
    }
    if (scroll) target.scrollIntoView({ behavior: 'instant', block: 'start' });
    return true;
  }

  #handleJump = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('a[data-star-product-jump]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    const hash = link.getAttribute('href');
    if (!this.#openAnchor(hash)) return;
    event.preventDefault();
    if (window.location.hash !== hash) window.history.pushState(null, '', hash);
  };

  #handleHashChange = () => {
    this.#openAnchor(window.location.hash);
  };

  /**
   * @param {Event & {detail?: {quantity?: number, cartLine?: number}}} event
   */
  #handleQuantityUpdate = (event) => {
    if (!(event.target instanceof Element) || event.detail?.cartLine) return;
    if (event.target.closest(EXCLUDED_PRODUCT_CONTEXT) || !event.target.closest('[data-star-pdp]')) return;

    this.#updateContextLinks(event.detail?.quantity);
  };

  /**
   * Keeps contextual links honest while a required size selection is pending.
   * @param {Event & {detail?: {required?: boolean, ready?: boolean}}} event
   */
  #handleSelectionChange = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-star-pdp]')) return;

    const quantityInput = event.target.querySelector('input[name="quantity"]');
    const currentQuantity = quantityInput instanceof HTMLInputElement ? quantityInput.value : this.dataset.quantity;
    this.#updateContextLinks(currentQuantity);
  };

  /**
   * Returns true only when this support panel represents the PDP's selected variant.
   * @returns {boolean}
   */
  #canUseVariantContext() {
    const pdp = document.querySelector('[data-star-pdp]');
    if (!(pdp instanceof HTMLElement) || pdp.dataset.starSelectionRequired !== 'true') return true;
    if (pdp.dataset.starSelectionReady !== 'true') return false;

    const supportVariantId = this.dataset.variantId?.trim() ?? '';
    const selectedVariantId = pdp.dataset.starSelectedVariantId?.trim() ?? '';
    if (supportVariantId && selectedVariantId) return supportVariantId === selectedVariantId;

    const urlVariantId = new URL(window.location.href).searchParams.get('variant')?.trim() ?? '';
    return Boolean(supportVariantId && urlVariantId && supportVariantId === urlVariantId);
  }

  /**
   * Adds the currently selected product, SKU and quantity to support destinations.
   * @param {number | string | undefined} quantityValue
   */
  #updateContextLinks(quantityValue) {
    const parsedQuantity = Number.parseInt(String(quantityValue ?? ''), 10);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity < 1) return;

    const productTitle = this.dataset.productTitle?.trim() ?? '';
    const sku = this.#canUseVariantContext() ? this.dataset.sku?.trim() ?? '' : '';
    this.dataset.quantity = String(parsedQuantity);

    for (const link of this.querySelectorAll('[data-star-product-support-action]')) {
      if (!(link instanceof HTMLAnchorElement)) continue;

      const baseUrl = link.dataset.actionBaseUrl || link.getAttribute('href');
      if (!baseUrl) continue;

      try {
        const actionUrl = new URL(baseUrl, window.location.origin);
        if (actionUrl.protocol !== 'http:' && actionUrl.protocol !== 'https:') continue;

        const action = link.dataset.starProductSupportAction ?? '';
        if (action === 'help' && !actionUrl.searchParams.has('reason')) {
          actionUrl.searchParams.set('reason', 'product-help');
        }
        if (action === 'document-request') {
          actionUrl.searchParams.set('reason', 'document-request');
        }

        if (productTitle) actionUrl.searchParams.set('product', productTitle);
        else actionUrl.searchParams.delete('product');

        if (sku) {
          actionUrl.searchParams.set('sku', sku);
          if (action === 'document-request') actionUrl.searchParams.set('reference', sku);
        } else {
          actionUrl.searchParams.delete('sku');
          actionUrl.searchParams.delete('reference');
        }

        actionUrl.searchParams.set('quantity', String(parsedQuantity));
        link.href = serializeActionUrl(actionUrl);
      } catch {
        // Leave merchant-configured destinations untouched when they are not valid URLs.
      }
    }

    const sizeHelpSelector = `[data-star-pdp-size-help][data-product-id="${CSS.escape(this.dataset.productId ?? '')}"]`;
    const sizeGuide = this.#canUseVariantContext() ? this.querySelector('#StarProductSizeGuide') : null;
    for (const link of document.querySelectorAll(sizeHelpSelector)) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      if (sizeGuide) {
        link.href = '#StarProductSizeGuide';
        link.textContent = link.dataset.guideLabel;
        link.setAttribute('data-star-product-jump', '');
      } else {
        const helpUrl = new URL(link.dataset.contactPath || '/pages/contact', window.location.origin);
        helpUrl.searchParams.set('reason', 'product-help');
        if (productTitle) helpUrl.searchParams.set('product', productTitle);
        if (sku) helpUrl.searchParams.set('sku', sku);
        helpUrl.searchParams.set('quantity', String(parsedQuantity));
        link.href = serializeActionUrl(helpUrl);
        link.textContent = link.dataset.helpLabel;
        link.removeAttribute('data-star-product-jump');
      }
    }
  }

  #handleProductSelect = (event) => {
    if (!isMainProductEvent(event)) return;

    const productId = this.dataset.productId ?? '';
    Promise.resolve(event.promise)
      .then((result) => {
        if (!this.isConnected) return;

        const detail = result?.detail;
        const html = detail?.html;
        if (!(html instanceof Document)) return;

        const eventProductId = String(detail.productId ?? '');
        const loadsNewProduct = Boolean(detail.newProduct);
        if (!loadsNewProduct && eventProductId && eventProductId !== productId) return;

        const sectionId = this.dataset.sectionId ?? '';
        if (!sectionId) return;

        const selector = `star-product-support[data-section-id="${CSS.escape(sectionId)}"]`;
        const newSupport = html.querySelector(selector);
        if (!(newSupport instanceof HTMLElement)) return;

        const newProductId = newSupport.dataset.productId ?? '';
        if (!loadsNewProduct && eventProductId && newProductId !== eventProductId) return;

        for (const detail of this.querySelectorAll('details[id]')) {
          const replacement = newSupport.querySelector(`#${CSS.escape(detail.id)}`);
          if (replacement instanceof HTMLDetailsElement) replacement.open = detail.open;
        }
        this.replaceWith(newSupport);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[star-product-support] Variant update failed:', error);
      });
  };
}

class StarPdpOverview extends HTMLElement {
  /** @type {AbortController | null} */
  #eventController = null;

  connectedCallback() {
    this.#eventController?.abort();
    this.#eventController = new AbortController();
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#eventController.signal,
    });
  }

  disconnectedCallback() {
    this.#eventController?.abort();
    this.#eventController = null;
  }

  #handleProductSelect = (event) => {
    if (!isMainProductEvent(event)) return;

    const productId = this.dataset.productId ?? '';
    Promise.resolve(event.promise)
      .then((result) => {
        if (!this.isConnected) return;

        const detail = result?.detail;
        const html = detail?.html;
        if (!(html instanceof Document)) return;

        const eventProductId = String(detail.productId ?? '');
        const loadsNewProduct = Boolean(detail.newProduct);
        if (!loadsNewProduct && eventProductId && eventProductId !== productId) return;

        const sectionId = this.dataset.sectionId ?? '';
        const blockId = this.dataset.blockId ?? '';
        if (!sectionId || !blockId) return;

        const selector = `star-pdp-overview[data-section-id="${CSS.escape(sectionId)}"][data-block-id="${CSS.escape(blockId)}"]`;
        const newOverview = html.querySelector(selector);
        if (!(newOverview instanceof HTMLElement)) return;

        const newProductId = newOverview.dataset.productId ?? '';
        if (!loadsNewProduct && eventProductId && newProductId !== eventProductId) return;

        this.replaceWith(newOverview);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[star-pdp-overview] Variant update failed:', error);
      });
  };
}

if (!customElements.get('star-product-support')) {
  customElements.define('star-product-support', StarProductSupport);
}

if (!customElements.get('star-pdp-overview')) {
  customElements.define('star-pdp-overview', StarPdpOverview);
}
